import React from 'react';
import {
    Zap, Rocket, Globe, X, Check
} from 'lucide-react';
import './EnterpriseDMS.css';

function FeaturesPage({ onBack }) {
    const cardStyle = {
        background: 'var(--bg-card, #1a1d2e)',
        border: '1px solid var(--border-color, #2a2a3e)',
        borderRadius: '16px',
        padding: '1.75rem',
        marginBottom: '1.5rem',
    };

    const sectionTitle = (icon, text, color = '#a855f7') => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
            <div style={{ background: `${color}22`, borderRadius: '8px', padding: '6px', display: 'flex' }}>
                {React.cloneElement(icon, { size: 18, style: { color } })}
            </div>
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{text}</h2>
        </div>
    );

    const AdvantageItem = ({ icon, title, text }) => (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '1rem' }}>
            <div style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }}>{icon}</div>
            <div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.7, lineHeight: 1.5 }}>{text}</div>
            </div>
        </div>
    );

    return (
        <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
                <div>
                    <h3 className="drawer-title">Platform Features</h3>
                    <p className="drawer-subtitle">Explore experimental tools and platform capabilities.</p>
                </div>
                <button onClick={onBack} className="drawer-close" title="Close">
                    <X size={20} />
                </button>
            </div>

            <div className="drawer-content">

                {/* ── Why DMS Platform? (The Edge) ───────────────────────── */}
                <div style={{ ...cardStyle, background: 'linear-gradient(145deg, rgba(30,41,59,0.9) 0%, rgba(15,23,42,0.95) 100%)', borderColor: 'rgba(99,102,241,0.3)', color: '#ffffff' }}>
                    {sectionTitle(<Globe />, 'Why DMS Platform vs. Native Cloud DMS?', '#818cf8')}

                    <p style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '1.5rem', lineHeight: 1.6 }}>
                        While tools like AWS DMS or GCP Datastream are powerful, they are designed to lock you into their respective ecosystems.
                        Our platform is built for the modern, multi-cloud enterprise.
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <AdvantageItem
                            icon={<Check size={16} />}
                            title="True Cross-Cloud & Multi-Cloud"
                            text="AWS DMS limits you when migrating to GCP or Azure. We orchestrate migrations across ANY cloud or on-prem environment seamlessly without vendor bias."
                        />
                        <AdvantageItem
                            icon={<Check size={16} />}
                            title="Zero Vendor Lock-in"
                            text="Run your data pipelines on your terms. We utilize open-source infrastructure (Debezium, Kafka) so you own your migration stack entirely."
                        />
                        <AdvantageItem
                            icon={<Check size={16} />}
                            title="Automated Infrastructure Orchestration"
                            text="No more manual networking. We automatically deploy complex Cross-Cloud VPN bridges and Bastion hosts via Terraform behind the scenes."
                        />
                        <AdvantageItem
                            icon={<Check size={16} />}
                            title="Unified Control Plane"
                            text="Manage migrations, monitor CDC streams, and handle VPN networks from a single, unified glassmorphic interface instead of jumping between 10 different AWS consoles."
                        />
                    </div>
                </div>

                {/* ── Releases & Roadmap ───────────────────────── */}
                <div style={cardStyle}>
                    {sectionTitle(<Rocket />, "Releases & Roadmap", '#a855f7')}

                    <div style={{ position: 'relative', paddingLeft: '1.5rem', borderLeft: '2px solid var(--border-color)', marginLeft: '0.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                        {/* v1.0 */}
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-1.85rem', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 10px rgba(16,185,129,0.5)' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981', background: 'rgba(16, 185, 129, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>v1.0 - Released</span>
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>AWS to GCP (PostgreSQL Only)</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '4px', lineHeight: 1.4 }}>
                                Fully automated logical data migration support from AWS to GCP. Supports PostgreSQL databases only.
                            </div>
                        </div>

                        {/* v1.1 */}
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-1.85rem', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#6366f1', boxShadow: '0 0 10px rgba(99,102,241,0.5)' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6366f1', background: 'rgba(99, 102, 241, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>v1.1 - Upcoming</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>Monday, July 13</span>
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>Azure to GCP (PostgreSQL Only)</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '4px', lineHeight: 1.4 }}>
                                Expansion of cloud provider source database migration support to include Azure to GCP. Supports PostgreSQL databases only.
                            </div>
                        </div>

                        {/* v2.0 */}
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-1.85rem', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#8b5cf6', boxShadow: '0 0 10px rgba(139,92,246,0.5)' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#8b5cf6', background: 'rgba(139, 92, 246, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>v2.0 - Upcoming</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>Thursday, July 16</span>
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>MySQL Database Support</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '4px', lineHeight: 1.4 }}>
                                Logical data migration support expanding beyond Postgres to full MySQL database engine restoration and replication.
                            </div>
                        </div>

                        {/* v2.1 */}
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', left: '-1.85rem', top: '4px', width: '12px', height: '12px', borderRadius: '50%', background: '#ec4899', boxShadow: '0 0 10px rgba(236,72,153,0.5)' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ec4899', background: 'rgba(236, 72, 153, 0.1)', padding: '2px 8px', borderRadius: '12px' }}>v2.1 - Upcoming</span>
                                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>Friday, July 31</span>
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: 700 }}>MongoDB Database Support</div>
                            <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '4px', lineHeight: 1.4 }}>
                                Direct cross-cloud support for NoSQL migrations, focusing on MongoDB clusters and collections.
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
}

export default FeaturesPage;
